import { Trans, useTranslation } from 'next-i18next'
// Dependencies.
import { useCallback, useContext, useState } from 'react'
import styled from 'styled-components'

// import TimezoneSelect from 'react-timezone-select'
import { useRouter } from 'next/router'

import { TimezoneSelect } from '../Inputs/TimezoneSelect'
import { H1 } from '../H1'
import { CTA } from '../CTA'
import { AuthenticateWithGoogleButton } from '../AuthenticateWithGoogleButton'
import { AuthenticateWithFacebookButton } from '../AuthenticateWithFacebookButton'
import { FormField } from '../Inputs/FormField'
import { P } from '../P'
import { Hyperlink } from '../Hyperlink'
import { UserContext, AnalyticsContext } from '../../context'
import { Input } from '../Inputs/Input'
import { stringFromQuery, colors } from '../../utils'
import { GooglereCAPTCHA } from '../GooglereCAPTCHA'
import { H2 } from '../H2'
import { PasswordInput } from '../Inputs/PasswordInput'

// Div.
const Div = styled.div`
  margin: auto;
  max-width: 90%;
  width: 493px;

  .header {
    margin-bottom: 37px;
  }

  .AuthenticateWithGoogleButton {
    margin-bottom: 20px;
  }

  .AuthenticateWithFacebookButton {
    margin-bottom: 20px;
  }

  .formField + .formField {
    margin-top: 30px;
  }

  .formField:last-child {
    margin-bottom: 40px;
  }

  .legalReference {
    color: #a6a6a6;
    margin-bottom: 40px;
    text-align: center;
  }

  .submitButton {
    margin-bottom: 31px;
  }

  .logInCTA {
    text-align: center;
  }

  .recaptchaContainer {
    margin-bottom: 27px;
  }

  .errorContainer {
    padding-right: 10px;
    padding-left: 10px;
    margin: auto;
  }

  .inputError {
    border-color: ${colors.red};
    opacity: 0.6;
  }

  .errorMessage {
    color: ${colors.red};
    text-align: center;
  }

  .alternative {
    text-align: center;
    margin-bottom: 20px;
  }
`

// Props.
interface Props {}

interface UserDetailError {
  email: string[]
  password1: string[]
  non_field_errors: string[]
  confirmEmail: string[]
  social: string[]
}

// Sign Up Form.
export const SignUpForm: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  // Router.
  const router = useRouter()
  const { query } = router
  const queryAsString = stringFromQuery(query)

  // Global State
  const { signUp, loginWithGoogle, loginWithFacebook } = useContext(UserContext)
  const { trackAccountRegistration } = useContext(AnalyticsContext)

  const socialUserAlreadyExistedError = t(
    'SignUpForm_socialUserAlreadyExistedError',
    'Account is already registered with this email using method of email and password',
  )

  // State.
  const [loading, setLoading] = useState(false)
  const [selectedTimezone, setSelectedTimezone] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [confirmEmailAddress, setConfirmEmailAddress] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [captchaReady, setCaptchaReady] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [formError, setFormError] = useState<UserDetailError>({
    email: [],
    password1: [],
    non_field_errors: [],
    confirmEmail: [],
    social: [],
  })

  // Form Is Submittable.
  const requiredFieldsArePresent =
    emailAddress && confirmEmailAddress && password && confirmPassword
  const formShouldBeSubmittable = requiredFieldsArePresent && !loading

  const validateDetailInput = (): boolean => {
    let result = true
    const currentFormError = { ...formError }

    if (!emailAddress || emailAddress === '') {
      result = false
      currentFormError.email = ['Required *']
    } else {
      currentFormError.email = []
    }

    if (!confirmEmailAddress || confirmEmailAddress === '') {
      result = false
      currentFormError.confirmEmail = [t('Required *', 'Required *')]
    } else {
      if (confirmEmailAddress !== emailAddress) {
        result = false
        currentFormError.confirmEmail = [
          t(
            'The two email address fields do not match',
            'The two email address fields do not match',
          ),
        ]
      } else {
        currentFormError.confirmEmail = []
      }
    }

    if (!password || password === '') {
      result = false
      currentFormError.password1 = [t('Required *', 'Required *')]
    } else {
      currentFormError.password1 = []
    }

    if (!confirmPassword || confirmPassword === '') {
      result = false
      currentFormError.non_field_errors = [t('Required *', 'Required *')]
    } else {
      if (confirmPassword !== password) {
        result = false
        currentFormError.non_field_errors = [
          t(
            'The two password fields do not match',
            'The two password fields do not match',
          ),
        ]
      } else {
        currentFormError.non_field_errors = []
      }
    }

    setFormError(currentFormError)
    return result
  }

  const resetError = (): UserDetailError => {
    return {
      email: [],
      confirmEmail: [],
      password1: [],
      non_field_errors: [],
      social: [],
    }
  }

  // On Submit.
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // Prevent Default.
    e.preventDefault()

    if (validateDetailInput()) {
      // Flag Loading.
      setLoading(true)
      const emailArray = emailAddress.split('@')
      await signUp({
        email: emailAddress,
        password1: password,
        password2: confirmPassword,
        full_name: emailArray[0],
      })
        .then(() => {
          trackAccountRegistration()
        })
        .catch((error) => {
          setFormError({ ...resetError(), ...error.response.data })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const onCaptchaReady = useCallback(() => {
    setCaptchaReady(true)
  }, [])
  const onCaptchaResponse = useCallback((token: string) => {
    setCaptchaToken(token)
  }, [])
  const onGoogleAuthSuccess = (token: string) => {
    loginWithGoogle({ access_token: token })
      .then(() => {
        trackAccountRegistration()
      })
      .catch((error) => {
        const message = error['non_field_errors']
        if (message) {
          setFormError({
            ...resetError(),
            ...{ social: [socialUserAlreadyExistedError] },
          })
        }
      })
  }

  const onFacebookAuthSuccess = (token: string) => {
    loginWithFacebook({ access_token: token })
      .then(() => {
        trackAccountRegistration()
      })
      .catch((error) => {
        const message = error['non_field_errors']
        if (message) {
          setFormError({
            ...resetError(),
            ...{ social: [socialUserAlreadyExistedError] },
          })
        }
      })
  }

  const onSocialButtonClicked = () => {
    setFormError(resetError())
  }

  // ..
  return (
    <Div>
      {/* Header. */}
      <H1 className="header">{t(`Create account`, `Create account`)}</H1>

      {/* Google. */}
      <AuthenticateWithGoogleButton
        onAuthSuccess={onGoogleAuthSuccess}
        onButtonClicked={onSocialButtonClicked}
        onAuthError={(error) => {
          setFormError({ ...resetError(), ...{ social: [error] } })
        }}
        buttonText={t(`SignUpForm1329791639`, `Sign up using Google`)}
      />

      {/* Facebook */}
      <AuthenticateWithFacebookButton
        onAuthSuccess={onFacebookAuthSuccess}
        onButtonClicked={onSocialButtonClicked}
        onAuthError={(error) => {
          setFormError({ ...resetError(), ...{ social: [error] } })
        }}
        buttonText={t(`SignUpForm695836580`, `Sign up using Facebook`)}
      />

      {formError.social.length > 0 && (
        <div className="errorContainer">
          {formError.social.map((error, index) => {
            return (
              <P key={index} className="detail errorMessage">
                {error}
              </P>
            )
          })}
        </div>
      )}
      <H2 className="alternative">{t(`Or`, `Or`)}</H2>

      {/* Form. */}
      <form className="signUpForm" onSubmit={onSubmit}>
        <div>
          {/* Email. */}
          <FormField required title={t(`Email address`, `Email address`)}>
            <Input
              className={formError.email.length > 0 ? 'inputError' : ''}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder={t(`Email`, `Email`)}
              type="email"
              value={emailAddress}
            />
            {formError.email.length > 0 && (
              <div className="errorContainer">
                {formError.email.map((error, index) => {
                  return (
                    <P key={index} className="detail errorMessage">
                      {error}
                    </P>
                  )
                })}
              </div>
            )}
          </FormField>

          {/* Confirm Email. */}
          <FormField
            required
            title={t(`SignUpForm-1257717264`, `Confirm email address`)}>
            <Input
              className={formError.confirmEmail.length > 0 ? 'inputError' : ''}
              onChange={(e) => setConfirmEmailAddress(e.target.value)}
              placeholder={t(`SignUpForm1848957358`, `Re-enter email address`)}
              type="email"
              value={confirmEmailAddress}
            />
            {formError.confirmEmail.length > 0 && (
              <div className="errorContainer">
                {formError.confirmEmail.map((error, index) => {
                  return (
                    <P key={index} className="detail errorMessage">
                      {error}
                    </P>
                  )
                })}
              </div>
            )}
          </FormField>

          {/* Create Password. */}
          <FormField required title={t(`Create password`, `Create password`)}>
            <PasswordInput
              errors={formError.password1}
              onChange={(e) => {
                setPassword(e.target.value)
                setFormError({
                  ...resetError(),
                })
              }}
              placeholder={t(`Password`, `Password`)}
              value={password}
            />
          </FormField>

          {/* Confirm Password. */}
          <FormField title={t(`Confirm password`, `Confirm password`)}>
            <PasswordInput
              errors={formError.non_field_errors}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t(`Re-enter password`, `Re-enter password`)}
              value={confirmPassword}
            />
          </FormField>
        </div>

        <div className="recaptchaContainer">
          <GooglereCAPTCHA
            onReady={onCaptchaReady}
            onResponse={onCaptchaResponse}
          />
        </div>

        {/* Privacy + Terms. */}
        <P className="legalReference detail">
          <Trans i18nKey="SignUpForm1897395021">
            By signing up, I agree to the Klyk{' '}
            <Hyperlink href="/privacy" newTab={true}>
              Privacy Policy
            </Hyperlink>{' '}
            and{' '}
            <Hyperlink href="/terms" newTab={true}>
              Terms of Service
            </Hyperlink>
            .
          </Trans>
        </P>

        {/* Submit Button. */}
        <CTA
          className="fullWidth purple submitButton"
          disabled={!formShouldBeSubmittable || captchaToken === ''}>
          {t(`Sign up`, `Sign up`)}
        </CTA>
      </form>

      {/* Log In CTA. */}
      <P className="callout logInCTA">
        {t(`SignUpForm1255722753`, `Already have an account?`)}
        <Hyperlink href={`/login${queryAsString}`}>
          {t(`Log in`, `Log in`)}
        </Hyperlink>
      </P>
    </Div>
  )
}
